import React, { useState, useEffect } from 'react';
import Panel from 'Components/Panel/Panel'
import ExecuteAxios from 'Components/ExecuteAxios/ExecuteAxios';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import PanelListDefault from 'Components/PanelListDefault/PanelListDefault';
import ApplyAttributes from 'Components/ApplyAttributes/ApplyAttributes';
import AutomaticallyListedAttributes from 'Components/AutomaticallyListedAttributes/AutomaticallyListedAttributes';
import ResetVariables from 'Components/ResetVariables/ResetVariables';
import RemoveIdsNotFound from 'Components/RemoveIdsNotFound/RemoveIdsNotFound';
import FilterItems from '../FilterItems/FilterItems';

const Client = ({
    registrationIdsSelected,
    clientIdSelected,
    onUpdateClientId
}) => {

    const [inserting, setInserting] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [loadingItemPanel, setLoadingItemPanel] = useState(null);
    const [recordsItemByUserId, setRecordsItemByUserId] = useState(null);
    const [attributesDetailItemPanel, setAttributesDetailItemPanel] = useState(null);
    const [isActiveFilter, setIsActiveFilter] = useState('S');

    const readOnlyAttributes = !(inserting || updating);

    const [id, setId] = useState(null);
    const [company, setCompany] = useState(null);
    const [cpfCnpj, setCpfCnpj] = useState(null);
    const [clientName, setClientName] = useState(null);
    const [city, setCity] = useState(null);
    const [state, setState] = useState(null);
    const [address, setAddress] = useState(null);
    const [addressNumber, setAddressNumber] = useState(null);
    const [postalCode, setPostalCode] = useState(null);
    const [province, setProvince] = useState(null);
    const [email, setEmail] = useState(null);
    const [phone, setPhone] = useState(null);
    const [isActive, setIsActive] = useState('S');
    const [dtUpdate, setDtUpdate] = useState(null);
    const [username, setUsername] = useState(null);
    const [creationDate, setCreationDate] = useState(null);
    const [userCreation, setUserCreation] = useState(null);

    const attributeList = [
        { name: "id", value: id, setter: setId },
        { name: "company", value: company, setter: setCompany },
        { name: "cpfCnpj", value: cpfCnpj, setter: setCpfCnpj },
        { name: "clientName", value: clientName, setter: setClientName },
        { name: "city", value: city, setter: setCity },
        { name: "state", value: state, setter: setState },
        { name: "address", value: address, setter: setAddress },
        { name: "addressNumber", value: addressNumber, setter: setAddressNumber },
        { name: "postalCode", value: postalCode, setter: setPostalCode },
        { name: "province", value: province, setter: setProvince },
        { name: "email", value: email, setter: setEmail },
        { name: "phone", value: phone, setter: setPhone },
        {
            name: "isActive",
            value: isActive,
            setter: setIsActive,
            resetValue: 'S',
            checks: [
                {
                    field: 'isActive',
                    condition: 'VALUE_EQUALS',
                    arrayValue: ['S']
                }
            ]
        },
        { name: "dtUpdate", value: dtUpdate, setter: setDtUpdate },
        { name: "username", value: username, setter: setUsername },
        { name: "creationDate", value: creationDate, setter: setCreationDate },
        { name: "userCreation", value: userCreation, setter: setUserCreation }
    ];

    useEffect(() => {
        reloadListItem()
    }, []);

    useEffect(() => {
        reloadListItem()
    }, [registrationIdsSelected, isActiveFilter]);

    useEffect(() => {
        setInserting(false)
        setUpdating(false)
        onUpdateClientId([])
    }, [isActiveFilter]);

    useEffect(() => {
        reloadListItem()

        if ((!clientIdSelected || clientIdSelected.length !== 1) && updating) {
            setUpdating(false)
        }

        if (!clientIdSelected || clientIdSelected.length > 1) {
            onUpdateClientId([])
        }

    }, [clientIdSelected, updating]);

    useEffect(() => {

        if (clientIdSelected && clientIdSelected.length === 1) {
            fetchDataDetail(clientIdSelected[0]);
        } else {
            fetchDataDetail(null);
        }

    }, [clientIdSelected]);

    const itemPanelStyle = {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '83%'
    };

    async function reloadListItem() {

        const parameters = { isActive: isActiveFilter }

        const items = await ExecuteAxios.api(
            'get',
            `/client/grid/`,
            { params: parameters }
        );

        setRecordsItemByUserId(items);

        RemoveIdsNotFound.remove(
            items,
            onUpdateClientId,
            clientIdSelected
        );
    }

    const fetchDataDetail = async (clientIdSelected) => {

        const parameters = {
            id: clientIdSelected ? clientIdSelected : 0
        }

        const item = await ExecuteAxios.api(
            'get',
            `/client/detail/`,
            { params: parameters }
        )

        if (item) {
            setAttributesDetailItemPanel(item)
            ApplyAttributes.applyAttributes(item, attributeList)
        }

        if (!clientIdSelected || clientIdSelected.length === 0) {
            ResetVariables.resetVariables(attributeList);
        }
    };

    const getItemPanelListOrDetail = () => {

        if (!updating && !inserting) {

            return (
                <PanelListDefault
                    onUpdateIdSelected={onUpdateClientId}
                    idsSelected={clientIdSelected}
                    data={recordsItemByUserId}
                    attributeList={attributeList}
                />
            )
        }

        return (
            <AutomaticallyListedAttributes
                list={attributesDetailItemPanel}
                attributeList={attributeList}
                readOnlyAttributes={readOnlyAttributes}
            />
        )
    }

    async function postItem() {

        try {

            setLoadingItemPanel(true);

            const response = await ExecuteAxios.api(
                'post',
                `/client/`,
                {
                    company,
                    cpfCnpj,
                    clientName,
                    city,
                    state,
                    address,
                    addressNumber,
                    postalCode,
                    province,
                    email,
                    phone,
                    isActive
                }
            );

            if (response) {
                await reloadListItem();
                setInserting(false);
            }

            return response;

        } finally {
            setLoadingItemPanel(false);
        }
    }

    async function putItem() {

        try {

            setLoadingItemPanel(true);

            const response = await ExecuteAxios.api(
                'put',
                `/client/`,
                {
                    id,
                    company,
                    cpfCnpj,
                    clientName,
                    city,
                    state,
                    address,
                    addressNumber,
                    postalCode,
                    province,
                    email,
                    phone,
                    isActive
                }
            );

            if (response) {
                await reloadListItem();
                setUpdating(false);
            }

            return response;

        } finally {
            setLoadingItemPanel(false);
        }
    }

    return (
        <Panel
            className={'main-modal'}
            style={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 'calc(50% - 15px)',
                marginLeft: '0px'
            }}>
            <LoadingIndicator loaded={!loadingItemPanel}>
                <FilterItems
                    isActiveFilter={isActiveFilter}
                    onUpdatesIsActiveFilter={setIsActiveFilter}
                />
                <Panel
                    style={itemPanelStyle}
                    titlePage={'Clientes'}
                    setUpdating={setUpdating}
                    setInserting={setInserting}
                    inserting={inserting}
                    updating={updating}
                    enableEdit={true}
                    canEdit={
                        clientIdSelected &&
                        clientIdSelected.length === 1 &&
                        registrationIdsSelected &&
                        registrationIdsSelected.length === 1
                    }
                    putFunction={putItem}
                    enableNewRecord={true}
                    canNewRecord={
                        registrationIdsSelected &&
                        registrationIdsSelected.length === 1
                    }
                    postFunction={postItem}
                    canViewCRUD={true}
                    readOnly={false}
                    recordsSelected={clientIdSelected}
                    viewOnlyOneRecordCRUD={true}
                    resetVariables={() => {
                        ResetVariables.resetVariables(attributeList)
                    }}
                    dataListedAttributes={attributesDetailItemPanel}
                    attributeList={attributeList}
                    onUpdateIdSelected={onUpdateClientId}
                >
                    {getItemPanelListOrDetail()}
                </Panel>
            </LoadingIndicator >
        </Panel >

    );

}

export default Client;